import React, { useState, useEffect } from 'react';
import buttonImage from '../buttonimage.webp';
import backgroundImage from '../background.webp';
import cardBackgroundImage from '../cardbackground.webp';
import mirrorimage from '../mirrorimage.webp';
import { supabase } from '../supabaseClient';

export default function HiddenBenefits({ goBack, goHome, navigateTo }) {
    const [showRestrictionModal, setShowRestrictionModal] = useState(null);

    useEffect(() => {
        const checkSession = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                setShowRestrictionModal({ message: 'Please log in to start a session.', redirect: 'login' });
                return;
            }

            const { data: profile, error: profileError } = await supabase
                .schema('api')
                .from('user_profiles')
                .select('membership_type, session_complete, session_start_date, created_at, next_membership_start')
                .eq('id', user.id)
                .single();

            if (profileError) {
                console.error('Error fetching profile:', profileError.message);
                setShowRestrictionModal({ message: 'Failed to load user profile.', redirect: 'login' });
                return;
            }

            console.log('Debug: Profile data:', {
                membership_type: profile.membership_type,
                session_complete: profile.session_complete,
                session_start_date: profile.session_start_date,
                created_at: profile.created_at,
                next_membership_start: profile.next_membership_start
            });

            if (profile.membership_type === 'Free Subscription') {
                const now = new Date();
                console.log('Debug: Current UTC time:', now.toISOString());

                // 24-hour timeout check for incomplete sessions
                if (profile.session_start_date && !profile.session_complete) {
                    const sessionStart = new Date(profile.session_start_date + 'Z');
                    const hoursSinceStart = (now - sessionStart) / (1000 * 60 * 60);
                    console.log('Debug: Session timeout check:', {
                        session_start_date: profile.session_start_date,
                        parsed_session_start: sessionStart.toISOString(),
                        hoursSinceStart: hoursSinceStart.toFixed(2),
                        is_timeout: hoursSinceStart > 24
                    });
                    if (hoursSinceStart > 24) {
                        const baseDate = profile.next_membership_start && new Date(profile.next_membership_start) <= now
                            ? new Date(Math.max(new Date(profile.created_at), new Date(profile.next_membership_start)))
                            : new Date(profile.created_at);
                        const daysSinceBase = Math.floor((now - baseDate) / (24 * 60 * 60 * 1000));
                        const monthsSinceBase = Math.floor(daysSinceBase / 30);
                        const resetDate = new Date(baseDate);
                        resetDate.setDate(resetDate.getDate() + (monthsSinceBase + 1) * 30);
                        setShowRestrictionModal({
                            message: `You've used your free monthly session. Upgrade to Monthly or Yearly for unlimited access. Your next free session resets on ${resetDate.toLocaleDateString()}.`,
                            redirect: 'back'
                        });
                        return;
                    }
                }

                // Monthly session limit check
                const baseDate = profile.next_membership_start && new Date(profile.next_membership_start) <= now
                    ? new Date(Math.max(new Date(profile.created_at), new Date(profile.next_membership_start)))
                    : new Date(profile.created_at);
                const daysSinceBase = Math.floor((now - baseDate) / (24 * 60 * 60 * 1000));
                const monthsSinceBase = Math.floor(daysSinceBase / 30);
                const resetDate = new Date(baseDate);
                resetDate.setDate(resetDate.getDate() + (monthsSinceBase + 1) * 30);

                if (profile.session_complete && daysSinceBase < (monthsSinceBase + 1) * 30) {
                    console.log('Debug: Monthly limit reached, reset date:', resetDate.toLocaleDateString());
                    setShowRestrictionModal({
                        message: `You've used your free monthly session. Upgrade to Monthly or Yearly for unlimited access. Your next free session resets on ${resetDate.toLocaleDateString()}.`,
                        redirect: 'back'
                    });
                    return;
                }

                // Update session_start_date for new sessions
                if (!profile.session_complete && !profile.session_start_date) {
                    try {
                        const { error: updateError } = await supabase
                            .schema('api')
                            .from('user_profiles')
                            .update({
                                session_start_date: new Date().toISOString(),
                            })
                            .eq('id', user.id);

                        if (updateError) {
                            console.error('Error updating session_start_date:', updateError.message);
                            setShowRestrictionModal({ message: 'Failed to start the session. Please try again.', redirect: 'back' });
                            return;
                        }

                        console.log('Session start date updated successfully.');
                    } catch (err) {
                        console.error('Unexpected error updating session_start_date:', err);
                        setShowRestrictionModal({ message: 'An unexpected error occurred while starting the session.', redirect: 'back' });
                        return;
                    }
                }
            }
        };

        checkSession();
    }, [navigateTo, goBack]);

    const handleSave = async () => {
        try {
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                alert('User not logged in!');
                return;
            }
            const { error: progressError } = await supabase
                .schema('api')
                .from('user_progress')
                .upsert({
                    user_id: user.id,
                    current_question_id: null,
                    current_component: 'healingFormulasPage/hiddenBenefits',
                    animated_helper: 'no', // <-- always set to 'no'
                    timestamp: new Date().toISOString(),
                });
            if (progressError) {
                alert('Failed to save progress: ' + progressError.message);
                return;
            }
            alert('Session progress has been saved successfully!');
        } catch (err) {
            alert('An unexpected error occurred while saving progress.');
        }
    };

    // Render modal if restriction or error occurs
    if (showRestrictionModal) {
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000
            }}>
                <div style={{
                    maxWidth: window.innerWidth <= 600 ? '90vw' : '400px',
                    width: '100%',
                    backgroundImage: `url(${cardBackgroundImage})`,
                    backgroundSize: 'cover',
                    border: '2px solid #4B5563',
                    borderRadius: '8px',
                    padding: '16px',
                    textAlign: 'center',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                    fontFamily: 'Times New Roman, Times, serif',
                    fontSize: window.innerWidth <= 600 ? '16px' : '18px',
                    color: '#4B5563',
                }}>
                    <p style={{ marginBottom: '16px', fontWeight: 'bold' }}>{showRestrictionModal.message}</p>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
                        {showRestrictionModal.redirect === 'back' && (
                            <button
                                style={{
                                    backgroundImage: `url(${buttonImage})`,
                                    backgroundSize: 'cover',
                                    border: '2px solid black',
                                    borderRadius: '10px',
                                    padding: window.innerWidth <= 600 ? '7px 0' : '10px 20px',
                                    width: window.innerWidth <= 600 ? '45vw' : '140px',
                                    fontFamily: 'Times New Roman, Times, serif',
                                    fontSize: window.innerWidth <= 600 ? '15px' : '16px',
                                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    console.log('Debug: Clicking Back button');
                                    setShowRestrictionModal(null);
                                    goBack();
                                }}
                            >
                                Back
                            </button>
                        )}
                        {(showRestrictionModal.redirect === 'back' || showRestrictionModal.redirect === null) && (
                            <button
                                style={{
                                    backgroundImage: `url(${buttonImage})`,
                                    backgroundSize: 'cover',
                                    border: '2px solid black',
                                    borderRadius: '10px',
                                    padding: window.innerWidth <= 600 ? '7px 0' : '10px 20px',
                                    width: window.innerWidth <= 600 ? '45vw' : '140px',
                                    fontFamily: 'Times New Roman, Times, serif',
                                    fontSize: window.innerWidth <= 600 ? '15px' : '16px',
                                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    console.log('Debug: Clicking My Account button, navigating to myAccount');
                                    setShowRestrictionModal(null);
                                    navigateTo('myAccount');
                                }}
                            >
                                My Account
                            </button>
                        )}
                        {showRestrictionModal.redirect && showRestrictionModal.redirect !== 'back' && showRestrictionModal.redirect !== null && (
                            <button
                                style={{
                                    backgroundImage: `url(${buttonImage})`,
                                    backgroundSize: 'cover',
                                    border: '2px solid black',
                                    borderRadius: '10px',
                                    padding: window.innerWidth <= 600 ? '7px 0' : '10px 20px',
                                    width: window.innerWidth <= 600 ? '45vw' : '140px',
                                    fontFamily: 'Times New Roman, Times, serif',
                                    fontSize: window.innerWidth <= 600 ? '15px' : '16px',
                                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    console.log('Debug: Clicking OK button, navigating to', showRestrictionModal.redirect);
                                    setShowRestrictionModal(null);
                                    navigateTo(showRestrictionModal.redirect);
                                }}
                            >
                                OK
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '16px',
        }}>
            <h3 style={{
                fontFamily: 'Times New Roman, Times, serif',
                fontSize: '24px',
                fontWeight: 'bold',
                color: 'black',
                marginBottom: '32px',
            }}>
                Hidden Benefits
            </h3>

            {/* Mirror Image Card */}
            <div
                style={{
                    width: '100%',
                    maxWidth: '600px',
                    borderRadius: '10px',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    border: '2px solid black',
                    marginBottom: '24px',
                    marginTop: 0,
                    overflow: 'hidden',
                    background: 'transparent',
                }}
            >
                <img
                    src={mirrorimage}
                    alt="Mirror"
                    style={{
                        width: '100%',
                        borderRadius: '10px 10px 0 0',
                        objectFit: 'cover',
                        maxHeight: '220px',
                    }}
                />
                <div
                    style={{
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        borderBottomLeftRadius: '10px',
                        borderBottomRightRadius: '10px',
                        padding: '24px',
                        textAlign: 'center',
                    }}
                >
                    <div style={{
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '22px',
                        fontWeight: 'bold',
                        color: '#4B5563',
                        margin: '0 0 8px 0',
                        textAlign: 'center',
                    }}>
                        Hidden Benefits
                    </div>
                    <div style={{
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        color: '#333',
                        textAlign: 'center',
                    }}>
                        There may be a hidden way that you are benefitting
                    </div>
                </div>
            </div>

            {/* Session Helper Cards Row */}
            <div
                className="hiddenbenefits-session-helper-row"
                style={{
                    display: 'flex',
                    flexDirection: window.innerWidth <= 600 ? 'column' : 'row',
                    gap: '16px',
                    width: '100%',
                    maxWidth: '600px',
                    marginBottom: '16px',
                }}
            >
                {/* Left Card */}
                <div style={{
                    flex: 1,
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '16px',
                    textAlign: 'left',
                    backgroundImage: `url(${cardBackgroundImage})`,
                    backgroundSize: 'cover',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    marginBottom: window.innerWidth <= 600 ? '16px' : 0,
                }}>
                    <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '18px', marginBottom: '10px', textAlign: 'center' }}>*Session Helper*</div>
                    <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '16px', margin: 0 }}>
                        <div>• The benefit we are getting may just be an avoidance of the fear of the unknown.</div>
                        <div>• The reason we call this a hidden benefit is because it is not in our awareness</div>
                        <div>• The hidden benefit indicates there is a subconscious reason we are keeping the pattern.</div>
                        <div>• We can use the reflection formula to release ourselves from a hidden benefit before we finish releasing our unwanted pattern.</div>
                        <div>• Sometimes it might not seem like an actual benefit but it is still a reason we avoid alleviating the pattern.</div>
                        <div>• If we are aware of a reason we keep the pattern, that can also be resolved using the formula.</div>
                    </div>
                </div>
                {/* Right Card */}
                <div style={{
                    flex: 1,
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '16px',
                    textAlign: 'left',
                    backgroundImage: `url(${cardBackgroundImage})`,
                    backgroundSize: 'cover',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                }}>
                    <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '18px', marginBottom: '10px', textAlign: 'center' }}>*Session Helper*</div>
                    <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '16px', margin: 0 }}>
                        <div>1. In what way do you benefit from having this pattern in your life?</div>
                        <div>2. Can you see another way to meet the need you are getting met with this pattern?</div>
                        <div>3. Is fear the reason you keep the pattern going?</div>
                        <div>4. What would be so bad about it if the pattern was gone?</div>
                        <div>5. Is the benefit you get from having the pattern something high on your list of values?</div>
                        <div>6. Are you willing to let go of the hidden benefit associated with this pattern?</div>
                    </div>
                </div>
            </div>

            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
                width: '100%',
                maxWidth: '600px',
            }}>
                <div style={{
                    width: '100%',
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '16px',
                    textAlign: 'center',
                    backgroundImage: `url(${cardBackgroundImage})`,
                    backgroundSize: 'cover',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                }}>
                    <p style={{
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        color: 'black',
                    }}>
                        Years ago I came across a concept shared by Teal Swan. She was pointing out that if we have a hard time resolving something within ourselves it may be because there is some way we benefit from having that pattern in our lives.  Overall the pattern may be unwanted, but our subconscious and ego know that we are getting something out of it being this way so it has us avoid resolving it.   Wow.  I remember the gratitude I felt for Teal Swan when she shared this in a video on her YouTube channel.   I made a lot of progress on some areas when I knew to look for hidden ways I might be benefitting from keeping an unwanted pattern.  Ever since then I have had this concept in my tool box and I used it as needed. 
                    </p>
                </div>

                <div style={{
                    width: '100%',
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '16px',
                    textAlign: 'center',
                    backgroundImage: `url(${cardBackgroundImage})`,
                    backgroundSize: 'cover',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                }}>
                    <p style={{
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        color: 'black',
                    }}>
                        In order to find the hidden benefit we have to be willing to do some self inquiry, self reflection and we need to be able to be really honest with ourselves.  Ask self, What am I getting out of continuing with this pattern?  What am I avoiding by contunuing this pattern? It may help to Joural about it in order to help us see what this avoidance has been about for us.  It is not wrong to have this as part of the pattern.  It can be really confusing to realize a pattern that is limiting us is also benefitting us.  That puts our energy at odds within ourselves and it can be quite uncomfortable and confusing to experience this.  It does take Bravery to face this in ourselves. 
                    </p>
                </div>

                <div style={{
                    width: '100%',
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '16px',
                    textAlign: 'center',
                    backgroundImage: `url(${cardBackgroundImage})`,
                    backgroundSize: 'cover',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                }}>
                    <p style={{
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        color: 'black',
                    }}>
                        Becoming aware of our reason for holding on to a pattern can often be enough to let go of it.  If we cannot easily let go we need to look for another way to fill that need.  We can also use the Acknowledge, Validate, Accept, and be Present formula for resolving the pattern of the hidden benefit once we are able to acknowledge it.  After we do that, we are ready to call the session complete. Do Not Complete The Session until you have visited each page to relect and process fully.
                    </p>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            width: '100%',
                            marginTop: '16px',
                        }}
                    >
                        <button
                            style={{
                                backgroundImage: `url(${buttonImage})`,
                                backgroundSize: 'cover',
                                border: '2px solid black',
                                borderRadius: '10px',
                                padding: '10px 20px',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap', // Prevent wrapping
                                margin: 0, // Remove extra margin
                                maxWidth: window.innerWidth <= 600 ? '95vw' : undefined,
                                boxShadow: '0 4px 16px rgba(0,0,0,0.22), 0 1.5px 4px rgba(0,0,0,0.15)' // <-- add shadow to button
                            }}
                            onClick={() => {
                                console.log('Debug: Clicking Session Completion button, navigating to endofSession');
                                navigateTo('endofSession');
                            }}
                        >
                            Session Completion
                        </button>
                    </div>
                </div>
            </div>

            <div
                className="hiddenbenefits-nav-btn-row"
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '16px',
                    marginTop: '32px',
                    marginBottom: '16px',
                }}
            >
                <button
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        width: '120px',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    }}
                    onClick={() => {
                        console.log('Debug: Clicking Back button');
                        goBack();
                    }}>Back</button>
                <button
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        width: '120px',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    }}
                    onClick={() => {
                        console.log('Debug: Clicking Home button');
                        goHome();
                    }}>Home</button>
                <button
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        width: '120px',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    }}
                    onClick={handleSave}
                >Save</button>
            </div>
            <style>
                {`
                @media (max-width: 600px) {
                    .hiddenbenefits-nav-btn-row {
                        position: static !important;
                        width: 100%;
                        background: none;
                        border-top: none;
                        border-radius: 0;
                        box-shadow: none;
                        padding: 0;
                        z-index: auto;
                        display: flex;
                        justify-content: center;
                        gap: 10px;
                        margin-top: 32px !important;
                        margin-bottom: 16px !important;
                    }
                    .session-completion-btn {
                        white-space: nowrap !important;
                        margin: 0 !important;
                        max-width: 95vw !important;
                        font-size: 15px !important;
                        padding-left: 0 !important;
                        padding-right: 0 !important;
                    }
                    body {
                        padding-bottom: 0 !important;
                    }
                }
                `}
            </style>
        </div>
    );
}